import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';
import { portfolio, portfolioFilters } from '../data/content';
import { usePageMeta } from '../components/ScrollToTop';

export default function Portfolio() {
  usePageMeta(
    'Portfolio & Case Studies — TheBigHub',
    'Explore TheBigHub\u2019s creative work across social media, digital advertising, SEO, website development, branding, content production, and digital training.'
  );
  const [filter, setFilter] = useState('All');
  const items = useMemo(
    () => (filter === 'All' ? portfolio : portfolio.filter((p) => p.category === filter)),
    [filter]
  );

  return (
    <main data-testid="portfolio-page">
      <PageHero
        eyebrow="Portfolio & Case Studies"
        title="Creative Work. Strategic Thinking. Real Projects."
        copy="A selection of projects across our eight core services — from brand identities and websites to campaigns, content, and training programs."
      />
      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
          <div className="mb-12 flex flex-wrap gap-2.5" data-testid="portfolio-filters" role="tablist" aria-label="Filter projects by service category">
            {portfolioFilters.map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={filter === f}
                data-testid={`portfolio-filter-${f.toLowerCase().replace(/[^a-z]/g, '-')}`}
                onClick={() => setFilter(f)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                  filter === f
                    ? 'bg-forest text-white shadow-[0_10px_28px_-12px_rgba(6,75,43,0.7)]'
                    : 'border border-ink/15 bg-white text-ink hover:border-forest hover:text-forest'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {items.map((p) => (
                <motion.article
                  layout
                  key={p.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`portfolio-item-${p.id}`}
                  className="card-lift group flex flex-col overflow-hidden rounded-2xl border border-ink/10 bg-white"
                >
                  <div className="img-frame aspect-[4/3] rounded-none">
                    <img src={p.image} alt={p.imageAlt} loading="lazy" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <span className="rounded-full bg-gold/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-forest">
                        {p.category}
                      </span>
                      <span className="text-xs text-ink">{p.client}</span>
                    </div>
                    <h2 className="font-display text-xl font-bold text-ink">{p.title}</h2>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-forest">{p.service}</p>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-ink">{p.overview}</p>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>

          <p className="mt-12 font-mono text-[11px] uppercase tracking-[0.2em] text-ink" data-testid="portfolio-sample-note">
            Showcase projects for illustration — verified client work, metrics, and testimonials are published with permission.
          </p>

          <div className="mt-14 rounded-2xl bg-pine p-8 sm:p-12">
            <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
              <div>
                <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">
                  Your Project Could Be Next.
                </h2>
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-white">
                  Tell us what you're building — we'll bring the strategy, creativity, and execution.
                </p>
              </div>
              <Link to="/contact" data-testid="portfolio-cta" className="btn-mint shrink-0">
                Start Your Project <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
      <CTASection />
    </main>
  );
}
